import { createContext, ReactNode, useState } from 'react'

import { useAxios } from '../../utilities/Requests/useAxios'
import { AccessRoleList } from '../ProvidersProvider/types'
import { BACObj } from './BACProvider'

export interface patientObj {
  patientId: number
  patientName: string
  firstName: string
  lastName: string
  emailAddress: string
  phoneNumber: string
  vrName: string
  dateOfBirth: string
  admissionDate: string
  deactivationDate: string
  mrn: string
  accessRoles: AccessRoleList | null
  bacHistory: BACObj[]
  isActive: boolean
  timeZone: string
  primaryDiagnosis?: string
  secondaryDiagnoses?: string
  otherDiagnoses?: string
  needsRandomDrugScreens?: boolean
  program?: string
  drugScreenChartData?: DrugScreenData
  lastCalibrationDate: string
  usesRemaining: number
  isTapering: boolean
  geneticsStatus?: string
}

export type PatientMoodObj = {
  id: number
  moodId: number
  moodValue: number
  recordDate: string
}

export type DrugScreenData = {
  needsRandomDrugScreens: boolean
  frequency?: 'Weekly' | 'Monthly'
  drugScreenDate?: string
  patientScheduled?: string
  completed: boolean
}

export interface PatientChartPageContextInterface {
  activeChartPatient: patientObj
  patientMoodRecords: PatientMoodObj[]
  setActiveChartPatient: React.Dispatch<React.SetStateAction<patientObj>>
  getPatientDetails: (patientId: number) => Promise<any>
  getPatientMoodRecords: (patientId: number) => Promise<any>
  registerForDrugScreens: (
    patientId: number,
    registering: boolean,
    frequency?: string
  ) => Promise<any>
  rerunGenetics: (patientId: number) => Promise<any>
}

export const PatientChartPageContext =
  createContext<PatientChartPageContextInterface | null>(null)

export const PatientChartPageProvider = ({
  children,
}: {
  children?: ReactNode
}) => {
  const [activeChartPatient, setActiveChartPatient] = useState<patientObj>({
    patientId: 0,
    patientName: '',
    firstName: '',
    lastName: '',
    emailAddress: '',
    phoneNumber: '',
    vrName: '',
    dateOfBirth: '',
    admissionDate: '',
    deactivationDate: '',
    mrn: '',
    accessRoles: [],
    bacHistory: [],
    isActive: false,
    timeZone: '',
    primaryDiagnosis: '',
    secondaryDiagnoses: '',
    otherDiagnoses: '',
    needsRandomDrugScreens: false,
    lastCalibrationDate: '',
    usesRemaining: 0,
    isTapering: false,
  })

  const [patientMoodRecords, setPatientMoodRecords] = useState([])

  const { fetch } = useAxios()

  const getPatientDetails = async (patientId: number) => {
    const { data } = await fetch({
      path: `Provider/GetPatientDetails?PatientId=${patientId}`,
    })

    if (data) {
      setActiveChartPatient(data)
      return data
    }
  }

  const getPatientMoodRecords = async (patientId: number) => {
    const { data } = await fetch({
      path: `MoodTracker/GetPatientMoodRecords?patientId=${patientId}`,
    })

    if (data) {
      const dataModified = data.map((moodRecord: PatientMoodObj) => {
        return {
          id: moodRecord.id,
          value: moodRecord.moodValue,
          moodId: moodRecord.moodId,
          recordDate: moodRecord.recordDate,
        }
      })
      setPatientMoodRecords(dataModified)
      console.log('getPatientMoodRecords-success')
      return dataModified
    }
  }

  const registerForDrugScreens = async (
    patientId: number,
    registering: boolean,
    frequency?: string
  ) => {
    const { data } = await fetch({
      path: `DrugScreening/RegisterPatientForRandomDrugScreens?patientId=${patientId}&registering=${registering}&frequency=${frequency}`,
      methodType: 'PUT',
    })

    await getPatientDetails(patientId)
  }

  const rerunGenetics = async (patientId: number) => {
    const { data } = await fetch({
      path: `GeneticKit/RerunGeneticsByPatientId?patientId=${patientId}`,
      methodType: 'POST',
    })

    await getPatientDetails(patientId)
  }

  return (
    <PatientChartPageContext.Provider
      value={{
        activeChartPatient,
        patientMoodRecords,
        setActiveChartPatient,
        getPatientDetails,
        getPatientMoodRecords,
        registerForDrugScreens,
        rerunGenetics,
      }}
    >
      {children}
    </PatientChartPageContext.Provider>
  )
}
